/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import { inEnum, isBoolean, isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { PlayerEvent, isPlayerEvent } from "../events/PlayerEvent.js"
import { AdBreak, AdUnit, isAdBreak, isAdUnit } from "./AdUnit.js";
import { Action } from "@ciaopeople/cp-shatsu/EventHandler/event-handler";
import { ExecutionId } from "../player/MediaResource.js";
import { VideoInfoRawData } from "../../model/video/VideoInfo.js";

export enum PlayerAdEventType {

    AD_WILL_REQUEST = "ad_will_request",
    AD_LOADED = "ad_loaded",
    AD_VIEWABILTY = "ad_viewabilty",
    AD_RESUMABLE_MEDIA = "ad_resumable_media",
    AD_PAUSED_MEDIA = "ad_paused_media",
    AD_COMPLETED = "ad_completed",
    AD_STARTED = "ad_started",
    AD_REQUESTED = "ad_requested", //ad unit
    AD_LOAD_ERROR = "ad_load_error", //ad unit
    AD_USER_CLOSE = "ad_user_close",
    AD_PLAY_ERROR = "ad_play_error",
    AD_CLICKED = "ad_clicked",
    AD_PAUSED = "ad_paused",
    AD_RESUMED = "ad_resumed"
};

/**
 * generic ad event
 */
export interface PlayerAdEvent extends PlayerEvent {

    eventType: PlayerAdEventType;
    executionId: ExecutionId;

}


export type PlayerAdEventListener = (e: PlayerAdEvent) => void;

export type PlayerAdEventAction = Action;

/**
 * type barrier for `PlayerAdEvent`
 * @param a value to check
 */
export function isPlayerAdEvent(a: any): a is PlayerAdEvent {
    return isPlayerEvent(a) && 
        ("eventType" in a) &&
        inEnum(a.eventType,PlayerAdEventType);
}

/**
 * ad unit related event
 */

export interface AdErrorStruct {
    fatal?: string,
    adunit?: string,
    vastErrorCode: number,
    type?: string,
    errorCode: number,
    message: string,
    adId?: string,
    adSystem?: string,
    contentType?: string,
    mediaUrl?: string,
    clickUrl?: string,
    duration?:  number,
    linear?: boolean,
    title?: string,
    description?: string,
    userAgent: string,
    connection: string,
    href: string,
    wrapperIds?: string[],
    wrapperSys?: string[]
    vinfo?: VideoInfoRawData

}


export interface AdInfoStruct {
    linear: boolean,
    duration: number
    adId: string,
    adSystem?: string
    title?: string
}


export function isAdInfoStruct(x: any): x is AdInfoStruct {
    return isObject(x) &&
        ("linear" in x && isBoolean(x.linear)) &&
        ("duration" in x && isNumber(x.duration)) &&
        ("adId" in x && isString(x.adId)) &&
        (!("adSystem" in x) || isString(x.adSystem)) &&
        (!("title" in x) || isString(x.title));
}

export interface PlayerAdUnitRelatedEvent extends PlayerAdEvent {
        adUnit: AdUnit;
        error?: boolean;
        errorData?: AdErrorStruct;
}

export function isPlayerAdUnitRelatedEvent(a: any): a is PlayerAdUnitRelatedEvent {
    return isPlayerAdEvent(a) &&
            ("adUnit" in a && isAdUnit(a.adUnit)) &&
            (!("error" in a) || isBoolean(a.error));
}

/**
 * ad break related event
 */
export interface PlayerAdBreakRelatedEvent extends PlayerAdUnitRelatedEvent {
        adBreak: AdBreak;
}


export function isPlayerAdBreakRelatedEvent(a: any): a is PlayerAdBreakRelatedEvent {
    return isPlayerAdUnitRelatedEvent(a) &&
            ("adBreak" in a && isAdBreak(a.adBreak));
}


export interface PlayerAdEventWithInfo extends PlayerAdBreakRelatedEvent {

    adInfo: AdInfoStruct
}




export function isPlayerAdEventWithInfo(x: any): x is PlayerAdEventWithInfo {
    return isPlayerAdBreakRelatedEvent(x) && 
        ("adInfo" in x && isAdInfoStruct(x.adInfo));
        
}


export interface PlayerAdStartedEvent extends PlayerAdEventWithInfo{
    eventType: PlayerAdEventType.AD_STARTED
}

export function isPlayerAdStartedEvent(x: any): x is PlayerAdStartedEvent {
    return isPlayerAdEventWithInfo(x) && x.eventType == PlayerAdEventType.AD_STARTED
}

export interface PlayerAdLoadedEvent extends PlayerAdEventWithInfo{
    eventType: PlayerAdEventType.AD_LOADED
}

export function isPlayerAdLoadedEvent(x: any): x is PlayerAdLoadedEvent {
    return isPlayerAdEventWithInfo(x) && x.eventType == PlayerAdEventType.AD_LOADED
}

export interface PlayerAdCompletedEvent extends PlayerAdEventWithInfo {
    eventType: PlayerAdEventType.AD_COMPLETED
}

export function isPlayerAdCompleteEvent(x: any): x is PlayerAdCompletedEvent {
    return isPlayerAdEventWithInfo(x) && x.eventType == PlayerAdEventType.AD_COMPLETED;
}


export interface PlayerAdResumedEvent extends PlayerAdEventWithInfo {
    eventType: PlayerAdEventType.AD_RESUMED
}


export function isPlayerAdResumedEvent(x: any): x is PlayerAdResumedEvent {
    return isPlayerAdEventWithInfo(x) && x.eventType == PlayerAdEventType.AD_RESUMED;
}


export interface PlayerAdPausedEvent extends PlayerAdEventWithInfo {
    eventType: PlayerAdEventType.AD_PAUSED
}


export function isPlayerAdPausedEvent(x: any): x is PlayerAdPausedEvent {
    return isPlayerAdEventWithInfo(x) && x.eventType == PlayerAdEventType.AD_PAUSED;
}